import { computed } from "vue";

export function usePieChart(props, chartWidth, chartHeight) {
  const pieRadius = computed(() => Math.min(chartWidth.value, chartHeight.value) / 2 - 2);

  // Generate pie data based on categoryCount (for pie, categories = slices)
  const pieData = computed(() => {
    const data = [];
    for (let i = 0; i < props.categoryCount; i++) {
      // Vary slice sizes for visual interest
      const baseValue = 100 / props.categoryCount;
      const variance = ((i % 3) - 1) * 5; // -5, 0, or +5
      data.push(Math.max(10, baseValue + variance));
    }
    return data;
  });

  function polarToCartesian(radius, angleInDegrees) {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
    return {
      x: radius * Math.cos(angleInRadians),
      y: radius * Math.sin(angleInRadians),
    };
  }

  function describeArc(radius, startAngle, endAngle) {
    const start = polarToCartesian(radius, endAngle);
    const end = polarToCartesian(radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;

    return ["M", 0, 0, "L", start.x, start.y, "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y, "Z"].join(" ");
  }

  function describeDonutArc(outerRadius, innerRadius, startAngle, endAngle) {
    const outerStart = polarToCartesian(outerRadius, endAngle);
    const outerEnd = polarToCartesian(outerRadius, startAngle);
    const innerStart = polarToCartesian(innerRadius, endAngle);
    const innerEnd = polarToCartesian(innerRadius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;

    return [
      "M",
      outerStart.x,
      outerStart.y,
      "A",
      outerRadius,
      outerRadius,
      0,
      largeArcFlag,
      0,
      outerEnd.x,
      outerEnd.y,
      "L",
      innerEnd.x,
      innerEnd.y,
      "A",
      innerRadius,
      innerRadius,
      0,
      largeArcFlag,
      1,
      innerStart.x,
      innerStart.y,
      "Z",
    ].join(" ");
  }

  const pieSlices = computed(() => {
    const total = pieData.value.reduce((a, b) => a + b, 0);
    let currentAngle = 0;

    return pieData.value.map((value) => {
      const startAngle = currentAngle;
      const sliceAngle = (value / total) * 360;
      currentAngle += sliceAngle;
      const midAngle = startAngle + sliceAngle / 2;
      const explodeOffset = polarToCartesian(4, midAngle);

      return {
        path: describeArc(pieRadius.value, startAngle, currentAngle),
        explodeTransform: `translate(${explodeOffset.x}, ${explodeOffset.y})`,
      };
    });
  });

  const doughnutSlices = computed(() => {
    const total = pieData.value.reduce((a, b) => a + b, 0);
    const outerRadius = pieRadius.value;
    const innerRadius = pieRadius.value * 0.5;
    let currentAngle = 0;

    return pieData.value.map((value) => {
      const startAngle = currentAngle;
      const sliceAngle = (value / total) * 360;
      currentAngle += sliceAngle;

      return {
        path: describeDonutArc(outerRadius, innerRadius, startAngle, currentAngle),
      };
    });
  });

  return { pieRadius, pieData, pieSlices, doughnutSlices, polarToCartesian };
}
